import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Calendar, Hand, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';

type Source = 'BOOKING_COM' | 'EXPEDIA_TAAP' | 'AGODA' | 'UNKNOWN';

interface QueueItem {
  id: string;
  status: 'OPEN' | 'CLAIMED' | 'DONE' | 'ABANDONED';
  requestedAt: string;
  claimedByUserId: string | null;
  holdId: string;
  source: Source;
  externalRef: string;
  guestName: string | null;
  propertyName: string | null;
  expectedChargeDate: string | null;
  expectedAmount: string | null;
  currency: string | null;
  freeCancelUntil: string | null;
}

const SOURCE_LABEL: Record<Source, string> = {
  BOOKING_COM: 'Booking.com',
  EXPEDIA_TAAP: 'Expedia TAAP',
  AGODA: 'Agoda',
  UNKNOWN: 'Unknown',
};

function formatMoney(a: string | null, c: string | null): string {
  if (!a) return '—';
  const n = parseFloat(a);
  if (!isFinite(n)) return a;
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: c ?? 'INR' }).format(n);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

export function CancelQueuePage() {
  const qc = useQueryClient();
  const { user } = useAuth();

  const list = useQuery<QueueItem[]>({
    queryKey: ['cancel-queue'],
    queryFn: () => api('/api/holds/cancel-queue'),
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Cancellation queue</h1>
        <p className="text-sm text-muted-foreground">
          Hold cancellations admin asked ops to handle. Claim one to mark it yours, then cancel
          the booking on the OTA portal and tap "Done" with a short note.
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          {list.isLoading ? (
            <div className="px-6 py-10 text-center text-sm text-muted-foreground">
              <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : (list.data?.length ?? 0) === 0 ? (
            <p className="px-6 py-10 text-center text-sm text-muted-foreground">
              No cancellations pending. Nice.
            </p>
          ) : (
            <ul className="divide-y">
              {list.data!.map((q) => (
                <QueueRow key={q.id} task={q} currentUserId={user?.id ?? ''} onChanged={() => qc.invalidateQueries({ queryKey: ['cancel-queue'] })} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function QueueRow({ task, currentUserId, onChanged }: { task: QueueItem; currentUserId: string; onChanged: () => void }) {
  const [note, setNote] = useState('');
  const [showDone, setShowDone] = useState(false);

  const claim = useMutation({
    mutationFn: () => api(`/api/holds/cancel-queue/${task.id}/claim`, { method: 'POST' }),
    onSuccess: onChanged,
  });
  const done = useMutation({
    mutationFn: () => api(`/api/holds/cancel-queue/${task.id}/done`, { method: 'POST', body: { note: note.trim() || null } }),
    onSuccess: onChanged,
  });

  const isMine = task.claimedByUserId === currentUserId;
  const cancelDays = daysUntil(task.freeCancelUntil);
  const cancelTone =
    cancelDays === null
      ? 'text-muted-foreground'
      : cancelDays < 0
        ? 'text-destructive'
        : cancelDays <= 1
          ? 'text-warning'
          : 'text-muted-foreground';

  return (
    <li className="px-5 py-4">
      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide">
              {SOURCE_LABEL[task.source]}
            </span>
            <span className="font-mono text-xs text-muted-foreground">#{task.externalRef}</span>
            {task.status === 'CLAIMED' ? (
              <span className="rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                {isMine ? 'Yours' : 'Claimed'}
              </span>
            ) : null}
          </div>
          <div className="mt-1 truncate text-sm font-medium">
            {task.guestName ?? '—'}
            {task.propertyName ? <span className="ml-2 text-muted-foreground">· {task.propertyName}</span> : null}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
            {task.expectedChargeDate ? (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                charges {formatDate(task.expectedChargeDate)}
              </span>
            ) : null}
            {task.freeCancelUntil ? (
              <span className={cancelTone}>
                {cancelDays !== null && cancelDays >= 0
                  ? `free cancel for ${cancelDays}d`
                  : 'free cancel window closed'}
              </span>
            ) : null}
          </div>
        </div>

        <div className="text-right">
          <div className="text-sm font-semibold tabular-nums">
            {formatMoney(task.expectedAmount, task.currency)}
          </div>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            {task.status === 'OPEN' ? (
              <Button size="sm" onClick={() => claim.mutate()} disabled={claim.isPending}>
                <Hand className="mr-1 h-3.5 w-3.5" />
                {claim.isPending ? 'Claiming…' : 'Claim'}
              </Button>
            ) : null}
            {isMine && task.status === 'CLAIMED' ? (
              <Button size="sm" variant="outline" onClick={() => setShowDone((s) => !s)}>
                <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                Done
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {showDone && isMine ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note — e.g. cancelled in Booking extranet, no fee"
            className="h-9 flex-1 text-xs"
          />
          <Button size="sm" onClick={() => done.mutate()} disabled={done.isPending}>
            {done.isPending ? 'Saving…' : 'Confirm done'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowDone(false)}>
            Cancel
          </Button>
        </div>
      ) : null}
    </li>
  );
}
